import React, { useState, useEffect, useRef } from 'react';
import { FaPen } from 'react-icons/fa6';
import toast from 'react-hot-toast';

import {
  fetchBotSteps,
  updateBotStepPrompt,
  getAuthenticatedApps,
} from '../../services/api';
import { DataState } from './../../context/DataProvider';
import { WORKFLOW_API_URL } from './../../services/workflowApi';
import { integrationData, dataSources, appImages } from '../../utils/constants';
import { authenticateApp } from '../../utils/auth';
import TriggerNodeModal from '../workflow3/TriggerNodeModal';

const apps_data = [...integrationData, ...dataSources];

const BotStepsEditModal = ({ botId, onClose }) => {
  const [botSteps, setBotSteps] = useState([]);
  const [editingStepId, setEditingStepId] = useState(null);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const { userGoogleId, bots } = DataState();
  const textareaRef = useRef(null);
  const [integratedApps, setIntegratedApps] = useState([]);

  // State for the Trigger View Modal
  const [triggers, setTriggers] = useState([]); // State for triggers
  const [isTriggerModalOpen, setIsTriggerModalOpen] = useState(false);
  const [currentTriggerData, setCurrentTriggerData] = useState(null);
  const [appEventsData, setAppEventsData] = useState([]);

  // Function to fetch data needed by TriggerNodeModal
  const fetchAppsandEventsData = async (appIntegrations) => {
    try {
      const response = await fetch(
        `${WORKFLOW_API_URL}/canvas/appsAndTriggers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userGoogleId}`,
          },
          credentials: 'include',
          body: JSON.stringify({ apps: appIntegrations }),
        },
      );
      if (!response.ok) throw new Error('Failed to fetch app event data');
      const data = await response.json();
      setAppEventsData(data.appsEventsData || []);
    } catch (error) {
      toast.error('Failed to fetch app and event data for triggers.');
    }
  };

  // Handler to open the trigger modal in view-only mode
  const handleViewTrigger = (triggerNode) => {
    setCurrentTriggerData(triggerNode);
    setIsTriggerModalOpen(true);
  };

  // Handle Reauthorize button click
  const handleIntegrateButtonClick = async (app) => {
    if (app) {
      setLoading(true);
      await authenticateApp(app.value, userGoogleId, () => setLoading(false));
    }
  };

  // Fetch bot steps and integrated apps when botId changes
  useEffect(() => {
    if (botId) {
      setLoading(true);
      fetchBotSteps(botId, userGoogleId)
        .then((response) => {
          if (response.error) {
            setLoading(false);
            return;
          }
          setBotSteps(response.steps);
          setTriggers(response.trigger || []); // Set triggers from response
          // Fetch app/event data needed for the modal
          fetchAppsandEventsData(
            response.selected_app_integration_options || [],
          );
          setLoading(false);
        })
        .catch(() => setLoading(false));

      getAuthenticatedApps(userGoogleId, botId)
        .then((response) => {
          if (response.error) {
            setLoading(false);
            return;
          }
          const integrated = response.apps;

          // Filter triggerOptions to separate integrated and non-integrated apps
          const integratedApps = apps_data.filter((app) =>
            integrated.includes(app.value),
          );
          let currentBot = null;
          for (const bot of bots) {
            if (bot.id === botId) {
              currentBot = bot;
              break;
            }
          }
          if (!currentBot) {
            return;
          }
          setIntegratedApps(
            integratedApps.map((app) => {
              // Find common functions between bot functionalities and app functions
              const selectedFunctions = app.functions.filter((func) =>
                currentBot.functionalities.includes(func),
              );

              // Return the updated app object with `selectedFunctions`
              return {
                ...app,
                selectedFunctions,
              };
            }),
          );
        })
        .catch((err) => toast.error('Failed to fetch apps'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  const handleEditClick = (stepId, currentPrompt) => {
    setEditingStepId(stepId);
    setEditedPrompt(currentPrompt);
    setTimeout(() => {
      handleTextareaChange(); // Adjust textarea height right after setting the prompt
    }, 0); // Use a timeout of 0 to wait until the next render cycle
  };

  const handleCancelEdit = () => {
    setEditingStepId(null);
    setEditedPrompt('');
  };

  const handleSubmitEdit = (stepId) => {
    updateBotStepPrompt(botId, stepId, userGoogleId, editedPrompt)
      .then((response) => {
        if (response.error) {
          return;
        }

        // Update the state with the new prompt
        setBotSteps((prevSteps) =>
          prevSteps.map((step) =>
            step.id === stepId ? { ...step, prompt: editedPrompt } : step,
          ),
        );
        handleCancelEdit();
      })
      .catch((err) => toast.error('Failed to update prompt'));
  };
  // Function to automatically resize textarea height
  const handleTextareaChange = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to shrink textarea if needed
      textarea.style.height = 'auto';
      // Set the height to scrollHeight to expand it to fit content
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 ${
        botId ? 'block' : 'hidden'
      } bg-black bg-opacity-50`}
    >
      <div className="relative mx-auto my-8 w-full max-w-3xl rounded-lg bg-[#1E1F20] p-8 text-zinc-100 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Edit Bot Steps</h2>
          <button
            className="text-gray-500 hover:text-gray-800"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="hide-sidebar mt-4 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center">
              <div className="spinner-border inline-block h-8 w-8 animate-spin rounded-full border-4"></div>
            </div>
          ) : (
            <>
              {/* Triggers Section */}
              <h3 className="mb-2 text-lg font-semibold text-zinc-200">
                Triggers:
              </h3>
              <div className="mb-6 flex flex-wrap gap-3">
                {triggers.length > 0 ? (
                  triggers.map((trigger) => (
                    <button
                      key={trigger.id}
                      onClick={() => handleViewTrigger(trigger)}
                      className="flex items-center gap-3 rounded-lg bg-zinc-900 p-2 text-left shadow-sm transition-colors duration-200 hover:bg-zinc-800"
                    >
                      <div className="rounded-md bg-zinc-100 p-1">
                        <img
                          src={
                            appImages[trigger.data.triggerOption] ||
                            '/assets/logo.png'
                          }
                          alt={trigger.data.label}
                          className="h-6 w-6"
                        />
                      </div>
                      <h4 className="text-sm font-semibold">
                        {trigger.data.label || 'Unnamed Trigger'}
                      </h4>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-zinc-400">
                    No triggers configured.
                  </p>
                )}
              </div>
              <h3 className="mb-4 text-lg font-semibold text-zinc-200">
                Integrated Apps:
              </h3>
              <ul className="mb-6 list-disc">
                {integratedApps.map((app, index) => (
                  <div
                    key={app.id}
                    className="flex items-center justify-between gap-3 rounded-lg bg-zinc-100 bg-opacity-10 p-2 shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex-shrink-0">
                        <div className="ml-2 rounded-lg bg-zinc-200 p-1">
                          <img
                            alt={app.label}
                            src={app.image}
                            className="h-8 w-8"
                          />
                        </div>
                      </span>
                      <div>
                        <h4 className="text-md font-semibold">{app.label}</h4>
                        <p className="text-sm ">
                          {app.selectedFunctions.join(', ')}
                        </p>
                      </div>
                    </div>

                    <button
                      className="btn btn-secondary mx-2 mr-2 rounded-lg bg-zinc-900 px-3 py-2 text-sm text-zinc-200 transition-all duration-300 hover:bg-zinc-800"
                      onClick={() => handleIntegrateButtonClick(app)}
                    >
                      Reauthorize
                    </button>
                  </div>
                ))}
              </ul>

              <h3 className="mb-4 text-lg font-semibold">Bot Steps:</h3>
              <ul className="divide-y divide-zinc-700">
                {botSteps.map((step) => (
                  <li key={step.id} className="py-3">
                    {editingStepId === step.id ? (
                      <div>
                        <textarea
                          ref={textareaRef}
                          value={editedPrompt}
                          onChange={(e) => {
                            setEditedPrompt(e.target.value);
                            handleTextareaChange();
                          }}
                          className="mb-4 w-full resize-none overflow-hidden rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm"
                          rows={4} // Use a single row initially
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={handleCancelEdit}
                            className="btn btn-secondary rounded-lg bg-zinc-900 px-3 py-2 text-sm text-zinc-200 transition-all duration-300 hover:bg-zinc-800"
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-secondary rounded-lg bg-zinc-900 px-3 py-2 text-sm text-zinc-200 transition-all duration-300 hover:bg-zinc-800"
                            onClick={() => handleSubmitEdit(step.id)}
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <strong>Step {step.step_index + 1}:</strong>{' '}
                          {step.step_title}
                          <p className="mt-1 text-sm text-gray-600">
                            {step.prompt}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            handleEditClick(step.id, step.step_prompt);
                          }}
                          className="btn btn-secondary mx-2 mr-2 rounded-lg p-2 text-sm text-zinc-200 hover:bg-zinc-900"
                        >
                          <FaPen />
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {isTriggerModalOpen && (
                <TriggerNodeModal
                  isOpen={isTriggerModalOpen}
                  onClose={() => setIsTriggerModalOpen(false)}
                  nodeData={currentTriggerData}
                  appEventData={appEventsData}
                  viewOnly={true} // <-- This is the key prop for read-only mode
                  isBot={true}
                />
              )}
            </>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            className="btn btn-secondary mx-2 mr-2 rounded-lg bg-zinc-900 px-3 py-2 text-sm text-zinc-200 transition-all duration-300 hover:bg-zinc-800"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BotStepsEditModal;
